'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { schedulePointerEventsCleanup } from '@/lib/utils/pointer-events-cleanup';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-300 data-[state=open]:duration-300',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    overlayClassName?: string;
    showCloseButton?: boolean;
  }
>(({ className, overlayClassName, children, showCloseButton = true, style, ...props }, ref) => {
  React.useEffect(() => {
    return () => schedulePointerEventsCleanup('Dialog');
  }, []);

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Standard modal shape, applied to every dialog in the app:
          //   - capped at 80% of the viewport height
          //   - the dialog itself scrolls, so nothing can ever be unreachable
          //   - DialogHeader/DialogFooter pin themselves to the top and bottom of
          //     that scroll area (see below), so the title and the action buttons
          //     stay visible while the content moves between them
          // Pinning lives on the header/footer rather than on a separate body
          // wrapper because many dialogs put a <form> around their content — a
          // sticky child keeps working through that nesting, a flex body region
          // would not without rewriting each one.
          // A caller can still override any of it; tailwind-merge lets the later
          // class win.
          'fixed left-[50%] top-[50%] z-50 grid max-h-[80vh] w-full max-w-lg gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-300 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-lg',
          className,
        )}
        style={{
          transform: 'translate(-50%, -50%)',
          ...style,
        }}
        {...props}
      >
        {children}
        {/* Stay above the sticky z-20 header so its title cannot intercept the X. */}
        {showCloseButton && (
          <DialogPrimitive.Close className='absolute right-4 top-4 z-30 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'>
            <X className='h-4 w-4' />
            <span className='sr-only'>Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Pinned to the top of the dialog's scroll area. The negative margins pull it
 * over DialogContent's padding so scrolling content passes underneath rather
 * than through the gutters, and the padding puts the text back where it was.
 * Dialogs that set their own padding (p-0) pass `mx-0 px-0 pt-0` to opt out.
 */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'sticky -top-6 z-20 -mx-6 -mt-6 flex flex-col space-y-1.5 bg-background px-6 pb-3 pt-6 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

/**
 * Pinned to the bottom of the dialog's scroll area so the actions stay reachable
 * without scrolling to the end of a long form. Same margin treatment as
 * DialogHeader.
 */
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'sticky -bottom-6 z-20 -mx-6 -mb-6 flex flex-col-reverse gap-2 border-t bg-background px-6 pb-6 pt-3 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  
  
  DialogTitle,
  DialogTrigger,
};
